import * as React from 'react';
import { useRef, useEffect, useMemo } from 'react';

let SchedulePolicy = function (SchedulePolicy) {
  SchedulePolicy["Immediately"] = "schedule-immediately";
  SchedulePolicy["OnDemand"] = "schedule-on-demand";
  return SchedulePolicy;
}({});
let ClearPolicy = function (ClearPolicy) {
  ClearPolicy["Resolve"] = "resolve-on-clear";
  ClearPolicy["Cancel"] = "cancel-on-clear";
  return ClearPolicy;
}({});
let ActionPolicy = function (ActionPolicy) {
  ActionPolicy["Reset"] = "reset";
  ActionPolicy["Passive"] = "passive";
  return ActionPolicy;
}({});

class Timeout {
  constructor(action, timeoutMs, schedulePolicy = SchedulePolicy.Immediately) {
    this._timeoutId = void 0;
    this._action = void 0;
    this._timeoutMs = void 0;
    if (typeof action !== "function") {
      throw new Error("Action must be a function");
    }
    if (timeoutMs < 0) {
      throw new Error("Timeout period must be >= 0");
    }
    this._action = action;
    this._timeoutMs = timeoutMs;
    if (schedulePolicy === SchedulePolicy.Immediately) {
      this.set();
    }
  }
  get isSet() {
    return this._timeoutId != null;
  }
  set() {
    if (this.isSet) {
      this.clear(ClearPolicy.Cancel);
    }
    this._timeoutId = setTimeout(() => this.clear(ClearPolicy.Resolve), this._timeoutMs);
  }
  clear(policy = ClearPolicy.Cancel) {
    const timeoutId = this._timeoutId;
    this._timeoutId = null;
    if (timeoutId == null) {
      return;
    }
    clearTimeout(timeoutId);
    if (policy === ClearPolicy.Resolve) {
      this._action();
    }
  }
}

class Interval {
  constructor(action, intervalMs, schedulePolicy = SchedulePolicy.Immediately) {
    this._intervalId = void 0;
    this._action = void 0;
    this._intervalMs = void 0;
    if (typeof action !== "function") {
      throw new Error("Action must be a function");
    }
    if (intervalMs < 1) {
      throw new Error("Interval period must be >= 1");
    }
    this._action = action;
    this._intervalMs = intervalMs;
    if (schedulePolicy === SchedulePolicy.Immediately) {
      this.set();
    }
  }
  get isSet() {
    return this._intervalId != null;
  }
  set() {
    if (this.isSet) {
      this.clear(ClearPolicy.Cancel);
    }
    this._intervalId = setInterval(() => this._action(), this._intervalMs);
  }
  clear(policy = ClearPolicy.Cancel) {
    const intervalId = this._intervalId;
    this._intervalId = null;
    if (intervalId == null) {
      return;
    }
    clearInterval(intervalId);
    if (policy === ClearPolicy.Resolve) {
      this._action();
    }
  }
}

class AnimationFrame {
  constructor(action, schedulePolicy = SchedulePolicy.Immediately) {
    this._animationFrameId = void 0;
    this._action = void 0;
    if (typeof action !== "function") {
      throw new Error("Action must be a function");
    }
    this._action = action;
    if (schedulePolicy === SchedulePolicy.Immediately) {
      this.set();
    }
  }
  get isSet() {
    return this._animationFrameId != null;
  }
  set() {
    if (this.isSet) {
      this.clear(ClearPolicy.Cancel);
    }
    this._animationFrameId = requestAnimationFrame(time => this.clear(ClearPolicy.Resolve, time));
  }
  clear(policy = ClearPolicy.Cancel, time) {
    const animationFrameId = this._animationFrameId;
    this._animationFrameId = null;
    if (animationFrameId == null) {
      return;
    }
    cancelAnimationFrame(animationFrameId);
    if (policy === ClearPolicy.Resolve) {
      this._action(time || performance.now());
    }
  }
}

class ActionScheduler {
  constructor() {
    this._disabled = false;
    this._registeredActions = [];
  }
  timeout(action, period, options) {
    if (this._disabled) {
      return ActionScheduler.NoopAction;
    }
    const timeout = new Timeout(action, period, options == null ? void 0 : options.schedulePolicy);
    this._registeredActions.push(() => timeout.clear(options == null ? void 0 : options.clearPolicy));
    return timeout;
  }
  interval(action, period, options) {
    if (this._disabled) {
      return ActionScheduler.NoopAction;
    }
    const interval = new Interval(action, period, options == null ? void 0 : options.schedulePolicy);
    this._registeredActions.push(() => interval.clear(options == null ? void 0 : options.clearPolicy));
    return interval;
  }
  animationFrame(action, options) {
    if (this._disabled) {
      return ActionScheduler.NoopAction;
    }
    const animationFrame = new AnimationFrame(action, options == null ? void 0 : options.schedulePolicy);
    this._registeredActions.push(() => animationFrame.clear(options == null ? void 0 : options.clearPolicy));
    return animationFrame;
  }
  clearAll() {
    const registered = [...this._registeredActions];
    this._registeredActions = [];
    registered.forEach(clearFn => clearFn());
  }
  disable() {
    this._disabled = true;
    this.clearAll();
  }
}
ActionScheduler.NoopAction = {
  set: () => {},
  get isSet() {
    return false;
  },
  clear: () => {}
};

class ActionSchedulerProvider extends React.Component {
  constructor(...args) {
    super(...args);
    this._actionScheduler = new ActionScheduler();
  }
  componentWillUnmount() {
    this._actionScheduler.disable();
  }
  render() {
    const {
      children
    } = this.props;
    return children(this._actionScheduler);
  }
}

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}

function withActionScheduler(WrappedComponent) {
  const displayName = `withActionScheduler(${WrappedComponent.displayName || WrappedComponent.name})`;
  const C = props => React.createElement(ActionSchedulerProvider, null, schedule => React.createElement(WrappedComponent, _extends({}, props, {
    schedule: schedule
  })));
  C.displayName = displayName;
  return C;
}

function useInterval(action, intervalMs, options = {}) {
  const {
    actionPolicy,
    clearPolicy,
    schedulePolicy
  } = options;
  const actionProxyRef = useRef(action);
  const intervalRef = useRef(null);
  if (typeof action !== "function") {
    throw new Error("Action must be a function");
  }
  if (action !== actionProxyRef.current) {
    actionProxyRef.current = action;
    if (actionPolicy === ActionPolicy.Reset) {
      var _intervalRef$current;
      (_intervalRef$current = intervalRef.current) == null || _intervalRef$current.set();
    }
  }
  useEffect(() => {
    intervalRef.current = new Interval(() => {
      actionProxyRef.current == null || actionProxyRef.current();
    }, intervalMs, schedulePolicy);
    return () => {
      var _intervalRef$current2;
      (_intervalRef$current2 = intervalRef.current) == null || _intervalRef$current2.clear(clearPolicy);
      intervalRef.current = null;
    };
  }, [intervalMs, clearPolicy, schedulePolicy]);
  const externalApi = useMemo(() => ({
    set: () => {
      var _intervalRef$current3;
      (_intervalRef$current3 = intervalRef.current) == null || _intervalRef$current3.set();
    },
    clear: (policy = clearPolicy) => {
      var _intervalRef$current4;
      (_intervalRef$current4 = intervalRef.current) == null || _intervalRef$current4.clear(policy);
    },
    get isSet() {
      var _intervalRef$current$, _intervalRef$current5;
      return (_intervalRef$current$ = (_intervalRef$current5 = intervalRef.current) == null ? void 0 : _intervalRef$current5.isSet) != null ? _intervalRef$current$ : false;
    }
  }), [clearPolicy]);
  return externalApi;
}

function useTimeout(action, timeoutMs, options = {}) {
  const {
    actionPolicy,
    clearPolicy,
    schedulePolicy
  } = options;
  const actionProxyRef = useRef(action);
  const timeoutRef = useRef(null);
  if (typeof action !== "function") {
    throw new Error("Action must be a function");
  }
  if (action !== actionProxyRef.current) {
    actionProxyRef.current = action;
    if (actionPolicy === ActionPolicy.Reset) {
      var _timeoutRef$current;
      (_timeoutRef$current = timeoutRef.current) == null || _timeoutRef$current.set();
    }
  }
  useEffect(() => {
    timeoutRef.current = new Timeout(() => {
      actionProxyRef.current == null || actionProxyRef.current();
    }, timeoutMs, schedulePolicy);
    return () => {
      var _timeoutRef$current2;
      (_timeoutRef$current2 = timeoutRef.current) == null || _timeoutRef$current2.clear(clearPolicy);
      timeoutRef.current = null;
    };
  }, [timeoutMs, clearPolicy, schedulePolicy]);
  const externalApi = useMemo(() => ({
    set: () => {
      var _timeoutRef$current3;
      (_timeoutRef$current3 = timeoutRef.current) == null || _timeoutRef$current3.set();
    },
    clear: (policy = clearPolicy) => {
      var _timeoutRef$current4;
      (_timeoutRef$current4 = timeoutRef.current) == null || _timeoutRef$current4.clear(policy);
    },
    get isSet() {
      var _timeoutRef$current$i, _timeoutRef$current5;
      return (_timeoutRef$current$i = (_timeoutRef$current5 = timeoutRef.current) == null ? void 0 : _timeoutRef$current5.isSet) != null ? _timeoutRef$current$i : false;
    }
  }), [clearPolicy]);
  return externalApi;
}

export { ActionPolicy, ActionSchedulerProvider, ClearPolicy, SchedulePolicy, useInterval, useTimeout, withActionScheduler };
